import React from 'react';
import { Button, Space, Popconfirm } from 'antd';
import type { Mode } from '../hooks/usePWH205Mode';

interface ActionBarProps {
  mode: Mode;
  hasRecord: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  saving: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  approvalStatus: string;
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onLast: () => void;
  onAdd: () => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onSubmitApproval: () => void;
  onApprove: () => void;
  onViewHistory: () => void;
}

const ActionBar: React.FC<ActionBarProps> = ({
  mode, hasRecord, hasPrev, hasNext, saving,
  canAdd, canEdit, canDelete, approvalStatus,
  onFirst, onPrev, onNext, onLast,
  onAdd, onEdit, onSave, onCancel, onDelete,
  onSubmitApproval, onApprove, onViewHistory,
}) => {
  const isEditing = mode === 'add' || mode === 'edit';
  // BR-U03: Only allow edit/delete in draft (N/empty) status
  const isDraft = !approvalStatus || approvalStatus === 'N' || approvalStatus === '';
  // BR-U03: Submit = send for approval when draft; Retract when status=D
  const canSubmit = hasRecord && !isEditing && isDraft;
  const canRetract = hasRecord && !isEditing && approvalStatus === 'D';

  return (
    <div className="pwh205-action-bar">
      <Space wrap>
        {/* Navigation */}
        {!isEditing && (
          <>
            <Button onClick={onFirst} disabled={!hasPrev}>|&#9668;</Button>
            <Button onClick={onPrev} disabled={!hasPrev}>&#9668;</Button>
            <Button onClick={onNext} disabled={!hasNext}>&#9658;</Button>
            <Button onClick={onLast} disabled={!hasNext}>&#9658;|</Button>
          </>
        )}

        {/* Action buttons */}
        {!isEditing ? (
          <>
            {canAdd && <Button type="primary" onClick={onAdd}>新增</Button>}
            {canEdit && hasRecord && isDraft && (
              <Button onClick={onEdit}>修改</Button>
            )}
            {canDelete && hasRecord && isDraft && (
              <Popconfirm
                title="確認刪除此請款單？"
                onConfirm={onDelete}
                okText="確認"
                cancelText="取消"
              >
                <Button danger data-acceptance-action="PWH205-ACT-DELETE">刪除</Button>
              </Popconfirm>
            )}
          </>
        ) : (
          <>
            {mode === 'add' ? (
              <Button
                type="primary"
                data-acceptance-action="PWH205-ACT-CREATE"
                onClick={onSave}
                loading={saving}
              >存檔</Button>
            ) : (
              <Button
                type="primary"
                data-acceptance-action="PWH205-ACT-UPDATE"
                onClick={onSave}
                loading={saving}
              >存檔</Button>
            )}
            <Button onClick={onCancel} disabled={saving}>還原</Button>
          </>
        )}

        {/* BR-F12: Submit/Retract approval */}
        {canSubmit && (
          <Popconfirm
            title="確認送審？"
            onConfirm={onSubmitApproval}
            okText="確認"
            cancelText="取消"
          >
            <Button data-acceptance-action="PWH205-ACT-SUBMIT">送審</Button>
          </Popconfirm>
        )}
        {canRetract && (
          <Popconfirm
            title="確認取回？"
            onConfirm={onSubmitApproval}
            okText="確認"
            cancelText="取消"
          >
            <Button data-acceptance-action="PWH205-ACT-WITHDRAW">取回</Button>
          </Popconfirm>
        )}
        {canRetract && (
          <Popconfirm
            title="確定要直接核准這張暫借款請款單？"
            onConfirm={onApprove}
            okText="核准"
            cancelText="取消"
          >
            <Button type="primary" data-acceptance-action="PWH205-ACT-APPROVE">直接核准</Button>
          </Popconfirm>
        )}

        {/* Audit History */}
        {hasRecord && !isEditing && (
          <Button onClick={onViewHistory}>審核歷史</Button>
        )}
      </Space>
    </div>
  );
};

export default ActionBar;
